'use client'

import React, { useEffect, useMemo, useState } from 'react'

const STORE_NUMBER = Number(
  process.env.NEXT_PUBLIC_STORE_NUMBER || process.env.STORE_NUMBER || 1
)

// 直接呼叫 API routes（不透過 lib）
async function loadQuestionTypes() {
  const resp = await fetch('/api/contact/get_QuestionType', {
    cache: 'no-store',
  })
  const json = await resp.json().catch(() => ({}))
  if (!resp.ok || json?.Error)
    throw new Error(json?.Error || `HTTP ${resp.status}`)
  return json?.Data ?? []
}

async function submitMessage(payload) {
  const resp = await fetch('/api/contact/add_CustomerMessage', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const json = await resp.json().catch(() => ({}))
  // 後端約定：回 { Error:"", TimeStamp:"..." }，有錯會回 Error
  if (!resp.ok || json?.Error) {
    throw new Error(json?.Error || `HTTP ${resp.status}`)
  }
  return json
}

export default function Contact() {
  // 下拉資料
  const [qTypes, setQTypes] = useState([]) // 原始樹狀資料
  const [parentID, setParentID] = useState(null) // 目前選到的 Parent ID
  const [childID, setChildID] = useState(null) // 目前選到的 Child ID

  // 表單欄位
  const [orderID, setOrderID] = useState('')
  const [content, setContent] = useState('')
  const [attachmentURL, setAttachmentURL] = useState('')

  // UI 狀態
  const [submitting, setSubmitting] = useState(false)
  const [tip, setTip] = useState('')

  // 取得 QuestionType 下拉
  useEffect(() => {
    ;(async () => {
      try {
        const data = await loadQuestionTypes()
        setQTypes(data)

        // 預設選第一個 parent & 第一個 child（如果有）
        if (data?.length) {
          const p = data[0]
          setParentID(p.QuestionTypeID ?? null)
          const firstChild = p.Children?.[0] ?? null
          setChildID(firstChild?.QuestionTypeID ?? null)
        }
      } catch (e) {
        console.error(e)
        setTip(String(e?.message || e))
      }
    })()
  }, [])

  // 目前可選的 child 列表（跟著 parentID 變）
  const childOptions = useMemo(() => {
    const p = qTypes.find((x) => x.QuestionTypeID === parentID)
    return p?.Children ?? []
  }, [qTypes, parentID])

  // 切換 Parent 時，重置 Child（若有子類，帶第一個）
  useEffect(() => {
    const firstChild = childOptions?.[0] ?? null
    setChildID(firstChild?.QuestionTypeID ?? null)
  }, [parentID]) // eslint-disable-line

  const onSubmit = async (e) => {
    e.preventDefault()
    setTip('')

    if (!content.trim()) {
      setTip('請輸入留言內容')
      return
    }

    // 取 MemberID：若站上有登入／cookie 可自行改寫，這裡先沒有就 0
    let memberID = 0
    try {
      // 你可以在這裡接你們的 session/cookie
      // const cookies = document.cookie; ...
      // memberID = ...
    } catch (_) {}

    // 送後端的 QuestionTypeID：有 child 就用 child，否則用 parent
    const finalQuestionTypeID = childID || parentID || 0

    const payload = {
      StoreNumber: STORE_NUMBER,
      MemberID: 1, // 隱藏欄位：沒有就 0
      OrderID: (orderID || '').trim(),
      StaffID: 0, // 隱藏欄位：空字串
      MessageType: 1, // 固定會員
      QuestionTypeID: finalQuestionTypeID,
      Content: content.trim(),
      AttachmentURL: (attachmentURL || '').trim(),
    }

    setSubmitting(true)
    try {
      await submitMessage(payload)
      setTip('已送出，我們會盡快回覆您。')
      // 清空可清的欄位
      setOrderID('')
      setContent('')
      setAttachmentURL('')
    } catch (e) {
      setTip(String(e?.message || e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="contact-us container">
      <div className="mw-930">
        <div className="contact-us__form">
          <form className="needs-validation" onSubmit={onSubmit}>
            {/* Order ID（非必填） */}
            {/* <div className="form-floating my-4">
              <input
                type="text"
                className="form-control"
                id="order_id"
                placeholder="Order ID"
                value={orderID}
                onChange={(e) => setOrderID(e.target.value)}
              />
              <label htmlFor="order_id">Order ID</label>
            </div> */}

            {/* Question Type - Parent */}
            <div className="row g-3">
              <div className="col-lg-6">
                <div className="form-floating my-2">
                  <select
                    className="form-select"
                    id="question_type_parent"
                    value={parentID ?? ''}
                    onChange={(e) =>
                      setParentID(Number(e.target.value) || null)
                    }
                  >
                    {qTypes.map((p) => (
                      <option key={p.QuestionTypeID} value={p.QuestionTypeID}>
                        {p.QuestionTypeName}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="question_type_parent">
                    Question Type (Parent)
                  </label>
                </div>
              </div>

              {/* Question Type - Child */}
              <div className="col-lg-6">
                <div className="form-floating my-2">
                  <select
                    className="form-select"
                    id="question_type_child"
                    value={childID ?? ''}
                    onChange={(e) => setChildID(Number(e.target.value) || null)}
                    disabled={!childOptions.length}
                  >
                    {childOptions.length ? (
                      childOptions.map((c) => (
                        <option key={c.QuestionTypeID} value={c.QuestionTypeID}>
                          {c.QuestionTypeName}
                        </option>
                      ))
                    ) : (
                      <option value="">（此大類無次分類）</option>
                    )}
                  </select>
                  <label htmlFor="question_type_child">
                    Question Type (Child)
                  </label>
                </div>
              </div>
            </div>

            {/* Content（必填） */}
            <div className="my-4">
              <textarea
                className="form-control form-control_gray"
                placeholder="Your Message"
                cols="30"
                rows="8"
                required
                value={content}
                onChange={(e) => setContent(e.target.value)}
              ></textarea>
            </div>

            {/* Attachment URL（非必填） */}
            {/* <div className="form-floating my-4">
              <input
                type="url"
                className="form-control"
                id="attachment_url"
                placeholder="Attachment URL"
                value={attachmentURL}
                onChange={(e) => setAttachmentURL(e.target.value)}
              />
              <label htmlFor="attachment_url">Attachment URL</label>
            </div> */}

            {/* 提示訊息 */}
            {tip && (
              <p className="text-danger" style={{ marginTop: 4 }}>
                {tip}
              </p>
            )}

            {/* Submit */}
            <div className="my-4">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'SUBMIT'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  )
}
